import { getWidget } from '../../../shared/widgets';
import React from 'react';
import { User } from 'oidc-client';
import {
  HEADER_SECTION_ID,
  widgetTypes,
} from '../../../builder/util/constants';
import { previewSelectedFilters } from 'shared/widgets/RepeaterWidget/RepeaterSidebarPlugin';
import { rowMatchesSelectedTags } from 'shared/widgets/RepeaterWidget/RepeaterUtils';

export interface WidgetData {
  id: string;
  dynamic?: boolean;
  fullWidth?: boolean;
  height: number;
  isParentFullWidth?: boolean;
  parentId?: string;
  type: string;
  visible?: boolean;
  width: number;
  x: number;
  y: number;
  content: React.ReactNode;
  children: string[];
  marginTop?: number;
  config?: any;
}

export function isSafari() {
  const ua = navigator.userAgent.toLowerCase();
  return ua.includes('safari') && !ua.includes('chrome');
}

export function adjustWidgetYLocationsForBanner(
  widgets: WidgetData[]
): WidgetData[] {
  const headerWidget = widgets.find(w => w.id === HEADER_SECTION_ID);
  const bannerWidget = widgets.find(
    widget => widget.type === widgetTypes.Banner
  );
  // Sticky banners don't occupy inline space, so other widgets must not be
  // pushed down for them — regardless of any stale marginTop value.
  const isBannerSticky =
    bannerWidget?.config?.placement === 'Sticky Bottom (Fixed to Screen)';
  return widgets.map(widget => {
    if (
      headerWidget &&
      bannerWidget &&
      !isBannerSticky &&
      widget.id !== HEADER_SECTION_ID &&
      !headerWidget.children.includes(widget.id) &&
      !bannerWidget.children.includes(widget.id) &&
      widget.id !== bannerWidget.id
    ) {
      return {
        ...widget,
        y: (widget.y ?? 0) + (widget.marginTop ?? 0),
      };
    } else {
      return widget;
    }
  });
}

export function isDynamicWidget(widgetType: string): boolean {
  const dynamicWidgetTypes = new Set([
    widgetTypes.AccordionWidget,
    widgetTypes.Application,
    widgetTypes.RichText,
    widgetTypes.Navigation,
    widgetTypes.Text,
    widgetTypes.Tabs,
    widgetTypes.Search,
  ]);
  return dynamicWidgetTypes.has(widgetType);
}

export function isChildOfFullWidthParent(
  widgetId: string,
  fullWidthParents: Set<string>,
  currentPage: PageVersion
): boolean {
  for (const parentId of fullWidthParents) {
    const parentWidget = currentPage.content[parentId];
    if (parentWidget.children.includes(widgetId)) {
      return true;
    }
  }
  return false;
}

export const processRepeaterWidgetData = (
  currentPage: PageVersion,
  updateCurrentPage?: (page: PageVersion) => void
): PageVersion => {
  if (!currentPage) return currentPage;

  const repeaterWidgets = Object.values(currentPage.content).filter(
    (widget: any) => widget?.type === widgetTypes.RepeaterWidget
  );

  if (repeaterWidgets.length === 0) return currentPage;

  let hasChanges = false;
  const updatedPage = { ...currentPage };
  updatedPage.content = { ...currentPage.content };

  repeaterWidgets.forEach((repeaterWidget: any) => {
    const repeaterData = repeaterWidget.config?.repeaterData;
    const containers = repeaterWidget.children;

    if (!repeaterData || !Array.isArray(repeaterData.data)) return;

    // Apply filtering logic for preview mode
    const filterBy = repeaterWidget.config?.filterBy;
    const enableFiltering = repeaterWidget.config?.enableFiltering;
    const selectedFilters = previewSelectedFilters[repeaterWidget.id];

    containers.forEach((containerId: string) => {
      const container = currentPage.content[containerId];
      if (!container) return;

      const dataMapping = repeaterWidget.config?.repeaterDataMapping || {};
      const rowIndex = dataMapping[containerId];
      const rowData = repeaterData?.data?.[rowIndex];

      // Handle container visibility for filtering
      let shouldBeVisible = true;

      if (
        enableFiltering &&
        filterBy &&
        Array.isArray(selectedFilters) &&
        rowData
      ) {
        // If no filters are selected (empty array), show all containers
        // If filters are selected, only show containers that match
        const hasNoFilters = selectedFilters.length === 0;
        const matchesFilter = rowMatchesSelectedTags(
          rowData[filterBy],
          selectedFilters
        );
        shouldBeVisible = hasNoFilters || matchesFilter;
      } else if (container.config?.visible === false) {
        shouldBeVisible = false;
      } else {
        console.log(
          'Container',
          containerId,
          'using default visibility (no filtering applied)'
        );
      }

      // Update container visibility if changed
      if (container.config?.visible !== shouldBeVisible) {
        updatedPage.content[containerId] = {
          ...container,
          config: {
            ...container.config,
            visible: shouldBeVisible,
          },
        };
        hasChanges = true;
      }

      // Only process content for visible containers
      if (
        !shouldBeVisible ||
        rowIndex == null ||
        !container.children ||
        !rowData
      )
        return;

      container.children.forEach((widgetId: string) => {
        const widget = currentPage.content[widgetId];
        if (!widget) return;

        const key = widget.config?.repeaterWidgetKey;
        if (!key || !(key in rowData)) return;

        const value = rowData[key];
        const currentValue =
          widget.type === widgetTypes.Text
            ? widget.config.text
            : widget.type === widgetTypes.Link
            ? widget.config.displayText
            : widget.type === widgetTypes.Image
            ? widget.config.backgroundImage?.url
            : widget.config.displayText;

        if (currentValue !== value) {
          const newConfig = { ...widget.config };

          if (widget.type === widgetTypes.Text) {
            newConfig.text = value;
          } else if (widget.type === widgetTypes.Link) {
            newConfig.displayText = value;
          } else if (widget.type === widgetTypes.Image) {
            newConfig.backgroundImage = {
              ...newConfig.backgroundImage,
              url: value,
            };
          } else {
            newConfig.displayText = value;
          }

          newConfig.visible = shouldBeVisible;

          updatedPage.content[widgetId] = {
            ...widget,
            config: newConfig,
          };
          hasChanges = true;
        } else if (widget.config?.visible !== shouldBeVisible) {
          updatedPage.content[widgetId] = {
            ...widget,
            config: {
              ...widget.config,
              visible: shouldBeVisible,
            },
          };
          hasChanges = true;
        }
      });
    });
  });

  if (hasChanges && typeof updateCurrentPage === 'function') {
    updateCurrentPage(updatedPage);
  }
  return hasChanges ? updatedPage : currentPage;
};

export function processWidgetData(
  currentPage: PageVersion,
  onUpdateConfig: (
    config: any,
    widgetId: string,
    defaultWidget: boolean
  ) => void,
  user: User,
  pages: Page[],
  siteId: number,
  site: Site,
  initiateLogout: () => void,
  history: any
): WidgetData[] {
  // Process repeater widget data first to handle filtering and data mapping
  const processedPage = processRepeaterWidgetData(currentPage);

  let widgets: WidgetData[] = [];
  let data = processedPage.content;
  const fullWidthParents = new Set<string>();

  const sidebarWidth = 200;

  for (const key in data) {
    const widget = processedPage.content[data[key].id];
    if (widget.id === 'root') {
      continue;
    }

    let xShift = 0;

    if (
      widget.type === widgetTypes.RepeaterWidget &&
      widget.config?.enableFiltering
    ) {
      xShift = -sidebarWidth;
    }

    if (
      widget.config.enableFullScreen ||
      widget.id === 'headerSection' ||
      widget.id === 'footerSection'
    ) {
      fullWidthParents.add(widget.id);
    }

    const WidgetComponent = getWidget(widget.type, true).components
      .render as React.FC<WidgetRenderProps<any> & { history: any }>;
    const isParentFullWidth = isChildOfFullWidthParent(
      widget.id,
      fullWidthParents,
      processedPage
    );

    const content = !widget.config.isCustomWidget ? (
      <WidgetComponent
        className={widget.id}
        currentPage={processedPage}
        widgetState={processedPage.content[widget.id]}
        onChange={onUpdateConfig}
        value={processedPage.content[widget.id].config}
        editing={false}
        siteId={siteId}
        pages={pages}
        user={user}
        initiateLogout={initiateLogout}
        site={site}
        history={history} // Add history prop
      />
    ) : (
      <></>
    );

    widgets.push({
      id: widget.id,
      type: widget.type,
      height: widget.config.height,
      width: widget.config.width,
      x: Math.round(widget.config.xLocation + xShift),
      y: Math.round(widget.config.yLocation),
      dynamic: isDynamicWidget(widget.type),
      fullWidth: widget.config.enableFullScreen,
      isParentFullWidth: isParentFullWidth,
      visible: widget.config.visible,
      children: widget.children,
      content: content,
      marginTop: widget.config.marginTop || 0,
      config: widget.config,
    });
  }

  // Adjust all widgets y position to account for the height of the banner widget
  widgets = adjustWidgetYLocationsForBanner(widgets);
  return widgets;
}
